import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import App from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { MediaProvider } from "./components/media";

import "./styles/tokens.css";
import "./styles/chrome.css";
import "./components/components.css";
import "./components/animations.css";

/**
 * Boot.
 *
 * Everything here runs at MODULE SCOPE, which is the one place a React
 * ErrorBoundary cannot help: if any of it throws, React never mounts and the
 * page is blank. So nothing below is allowed to throw — each failure renders
 * something that explains itself instead.
 *
 * `index.html` carries a second failsafe for the case where this file never
 * runs at all (blocked bundle, unsupported syntax).
 */

function fatal(title: string, detail: string) {
  const root = document.getElementById("root");
  if (!root) return;
  root.innerHTML =
    '<div style="max-width:640px;margin:12vh auto;padding:24px;' +
    'font-family:system-ui,sans-serif;color:#e9ebef">' +
    '<h1 style="font-size:26px;margin:0 0 12px">' +
    title +
    "</h1>" +
    '<p style="line-height:1.5;margin:0 0 12px">' +
    detail +
    "</p></div>";
}

const container = document.getElementById("root");

if (!container) {
  // Nothing to render into; there is no recovery and nowhere to say so.
  console.error("Badly Dubbed: no #root element in index.html.");
} else {
  /**
   * The deployment this build talks to.
   *
   * `VITE_CONVEX_URL` is inlined at build time, which means any build made
   * without the variable present produces a bundle that cannot reach the
   * backend at all — and the failure surfaces to players, not to whoever ran
   * the build. Netlify has it set, `.env.local` has it set, and the dev server
   * has it set, but "every build path currently happens to be configured" is
   * not a guarantee, and the failure mode is a dead site.
   *
   * So: fall back to the known deployment rather than refusing to start. This
   * URL is public — it is in every built bundle already and is not a secret.
   * Override it per-environment with VITE_CONVEX_URL as usual.
   */
  const FALLBACK_CONVEX_URL = "https://earnest-canary-728.convex.cloud";
  const configured = import.meta.env.VITE_CONVEX_URL as string | undefined;
  const convexUrl = configured && configured.trim() ? configured.trim() : FALLBACK_CONVEX_URL;

  if (!configured) {
    console.warn(
      "VITE_CONVEX_URL was not set at build time; using the default deployment.",
    );
  }

  let convex: ConvexReactClient | null = null;
  let failure: string | null = null;

  {
    try {
      convex = new ConvexReactClient(convexUrl);
    } catch (err) {
      // Reached when something blocks the client outright — a script blocker,
      // strict tracking protection, or a browser with no WebSocket support.
      // Previously this threw at module scope and left a blank page.
      failure =
        "The connection to the game server was blocked before it could open. " +
        "An ad blocker, strict tracking protection, or a script blocker is the " +
        "usual cause — try a private window, or allow this site. (" +
        (err instanceof Error ? err.message : String(err)) +
        ")";
    }
  }

  if (!convex) {
    fatal("Badly Dubbed could not start", failure ?? "Unknown startup failure.");
  } else {
    /**
     * Clerk is OPTIONAL infrastructure here.
     *
     * Anonymous play is the default and must survive Clerk being unconfigured,
     * unreachable, or blocked — a party game whose join flow depends on an auth
     * provider being up is a worse game. So a missing publishable key drops us
     * to the plain Convex provider rather than failing to start.
     */
    const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

    const tree = (
      <MediaProvider>
        <App />
      </MediaProvider>
    );

    createRoot(container).render(
      <StrictMode>
        <ErrorBoundary>
          {clerkKey ? (
            <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
              {/* Hands Convex the Clerk token so ctx.auth.getUserIdentity()
                  resolves server-side. Anonymous players simply present no
                  token and keep using their bearer seat. */}
              <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
                {tree}
              </ConvexProviderWithClerk>
            </ClerkProvider>
          ) : (
            <ConvexProviderWithClerk
              client={convex}
              useAuth={(() => ({
                isLoaded: true,
                isSignedIn: false,
                getToken: async () => null,
                orgId: undefined,
                orgRole: undefined,
              })) as unknown as typeof useAuth}
            >
              {tree}
            </ConvexProviderWithClerk>
          )}
        </ErrorBoundary>
      </StrictMode>,
    );
  }
}
